import { useMemo, useState } from "react";
import { addressFilters, addresses } from "@/mocks/home";

export default function AddressSection() {
  const [filter, setFilter] = useState<string>(addressFilters[0]);
  const [query, setQuery] = useState("");
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    return addresses.filter((item) => {
      const matchRegion = filter === "全部线路" || item.region === filter;
      const matchQuery =
        q === "" ||
        item.name.toLowerCase().includes(q) ||
        item.url.toLowerCase().includes(q) ||
        item.tag.toLowerCase().includes(q);
      return matchRegion && matchQuery;
    });
  }, [filter, query]);

  const handleCopy = async (id: number, url: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopiedId(id);
      window.setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 2000);
    } catch {
      setCopiedId(null);
    }
  };

  const onlineCount = addresses.filter((a) => a.status === "在线").length;

  return (
    <section id="address" className="border-t border-background-200 bg-background-100 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <span className="text-xs font-medium uppercase tracking-widest text-primary-600">
              最新地址
            </span>
            <h2 className="mt-3 font-heading text-3xl text-foreground-950 md:text-4xl">
              51视频最新地址与可用线路
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base">
              下表收录当前线路及其状态，标记为「在线」的线路可直接使用。
              点击复制按钮即可拿到地址，在浏览器打开；若某条线路加载缓慢，换用其它在线线路即可。
            </p>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-background-200 bg-background-50 px-4 py-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
              <i className="ri-pulse-line text-xl" aria-hidden="true"></i>
            </span>
            <div>
              <p className="font-heading text-xl text-foreground-950">
                {onlineCount} / {addresses.length}
              </p>
              <p className="text-xs text-foreground-600">当前在线线路</p>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-50 p-1">
            {addressFilters.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  filter === f
                    ? "bg-primary-500 text-background-50"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="relative w-full lg:w-72">
            <span className="pointer-events-none absolute left-3 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base" aria-hidden="true"></i>
            </span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索线路名称或域名"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-500 focus:border-primary-400"
            />
          </div>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-background-200 bg-background-50">
          <div className="hidden grid-cols-12 gap-3 border-b border-background-200 bg-background-100 px-5 py-3 text-xs font-medium text-foreground-600 lg:grid">
            <span className="col-span-4">线路名称</span>
            <span className="col-span-3">访问地址</span>
            <span className="col-span-2">状态 / 延迟</span>
            <span className="col-span-2">更新时间</span>
            <span className="col-span-1 text-right">操作</span>
          </div>

          {list.length === 0 ? (
            <div className="flex flex-col items-center gap-2 px-5 py-14 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-500">
                <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
              </span>
              <p className="text-sm text-foreground-700">没有匹配的线路，请更换筛选或关键词</p>
              <button
                type="button"
                onClick={() => {
                  setFilter(addressFilters[0]);
                  setQuery("");
                }}
                className="mt-1 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                重置筛选
              </button>
            </div>
          ) : (
            <ul>
              {list.map((item) => (
                <li
                  key={item.id}
                  className="grid grid-cols-1 gap-3 border-b border-background-200 px-5 py-4 transition-colors last:border-b-0 hover:bg-background-100 lg:grid-cols-12 lg:items-center"
                >
                  <div className="lg:col-span-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-foreground-950">{item.name}</span>
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-900">
                        {item.tag}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-foreground-600 lg:hidden">{item.region}</p>
                  </div>

                  <div className="lg:col-span-3">
                    <code className="block truncate rounded-md bg-background-100 px-2.5 py-1.5 text-xs text-foreground-700 lg:bg-transparent lg:px-0 lg:py-0">
                      {item.url}
                    </code>
                  </div>

                  <div className="flex items-center gap-3 lg:col-span-2">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs ${
                        item.status === "在线"
                          ? "bg-accent-100 text-accent-900"
                          : "bg-secondary-200 text-secondary-900"
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${
                          item.status === "在线" ? "bg-accent-500" : "bg-secondary-500"
                        }`}
                      ></span>
                      {item.status}
                    </span>
                    <span className="text-xs text-foreground-600">{item.latency}</span>
                  </div>

                  <div className="text-xs text-foreground-600 lg:col-span-2">{item.updated}</div>

                  <div className="lg:col-span-1 lg:text-right">
                    <button
                      type="button"
                      onClick={() => handleCopy(item.id, item.url)}
                      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                        copiedId === item.id
                          ? "bg-accent-500 text-background-50"
                          : "bg-primary-500 text-background-50 hover:bg-primary-600"
                      }`}
                    >
                      <i
                        className={copiedId === item.id ? "ri-check-line" : "ri-file-copy-line"}
                        aria-hidden="true"
                      ></i>
                      {copiedId === item.id ? "已复制" : "复制"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-4 text-xs leading-relaxed text-foreground-600">
          说明：以上地址为本站收录的线路示例，会不定期轮换与更新；延迟数值为参考测速值，
          实际体验受所在地区与网络环境影响。请以打开页面的实际可用性为准。
        </p>
      </div>
    </section>
  );
}